import { useEffect } from 'react';
import { useBootstrap } from '../auth/use-bootstrap';
import type { ThemeId } from '../../api/endpoints/me';

/**
 * Theme orchestration — czyta `theme` z bootstrap response, ustawia `data-theme="alicja"|"bonus"`
 * na `<html>`. globals.css ma `:root[data-theme="bonus"]` overrides semantic CSS variables
 * (bg-primary, text-primary, border, etc.) — efekt instant po patch /me { theme }.
 *
 * **Persistence:** authoritative source = backend (Users.Theme). Localstorage fallback
 * (THEME_LS_KEY) zapobiega FOUC dla returning user przed bootstrap response — wstępna wartość
 * z poprzedniej sesji aplikuje się synchronicznie w main.tsx (applyInitialTheme), bootstrap
 * potem ewentualnie koryguje (np. user zmienił theme na innym urządzeniu).
 *
 * Domyślnie 'alicja' (dark) — gdy brak data w bootstrap (anon, loading, error).
 */
const THEME_LS_KEY = 'fiszki.theme';
const DEFAULT_THEME: ThemeId = 'alicja';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { data } = useBootstrap();
  const theme: ThemeId = data?.theme === 'bonus' ? 'bonus' : DEFAULT_THEME;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      window.localStorage.setItem(THEME_LS_KEY, theme);
    } catch {
      // localStorage może być zablokowane (private mode, quota) — bezpiecznie pomijamy.
    }
  }, [theme]);

  return <>{children}</>;
}

/**
 * Synchroniczny initial apply z localStorage — wywoływany w main.tsx PRZED React render,
 * żeby uniknąć FOUC (flash of unstyled / wrong theme content) gdy returning user otwiera app.
 *
 * Bootstrap response (async) później koryguje przez ThemeProvider effect.
 */
export function applyInitialThemeFromLocalStorage(): void {
  try {
    const cached = window.localStorage.getItem(THEME_LS_KEY);
    const theme: ThemeId = cached === 'bonus' ? 'bonus' : DEFAULT_THEME;
    document.documentElement.setAttribute('data-theme', theme);
  } catch {
    document.documentElement.setAttribute('data-theme', DEFAULT_THEME);
  }
}
